import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

// Use sessionStorage so each browser tab has its own isolated session.
// This prevents cross-tab account mixup when different accounts are
// logged in across multiple tabs (common on shared Render domains).
const storage = window.sessionStorage;

// ─── Clear stale API responses from the service worker cache ──
// When auth state changes (login/logout), the SW cache may contain
// API responses from a previous session. Delete them to prevent
// one user's data leaking to another via cache-first strategy.
const clearApiCache = async () => {
  if (!('caches' in self)) return;
  try {
    const cache = await caches.open('getnextern-v1');
    const keys = await cache.keys();
    const apiRequests = keys.filter((r) => r.url.includes('/api/'));
    await Promise.all(apiRequests.map((r) => cache.delete(r)));
  } catch {
    // SW cache may not be available — ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = storage.getItem('getnextern_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.getMe();
      setUser(res.data.user);
    } catch {
      storage.removeItem('getnextern_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (token, userData) => {
    storage.setItem('getnextern_token', token);
    setUser(userData);
    await clearApiCache();
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (_) {}
    storage.removeItem('getnextern_token');
    setUser(null);
    await clearApiCache();
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};